import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  alpha,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  format,
  addDays,
  subDays,
  startOfDay,
  eachDayOfInterval,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  Close,
  AccessTime,
  VideoCameraFront,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Star,
} from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import ChatReservation from './ChatReservation';
const SIDEBAR_WIDTH = 250;
const CELL_HEIGHT = 110;

const PlanningPro = () => {
  const scrollContainerRef = useRef(null);
  const [vue, setVue] = useState(7);
  const [currentMonth, setCurrentMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // États pour les données dynamiques
  const [offres, setOffres] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const [intervenants, setIntervenants] = useState([]);
  const [selectedIntervenant, setSelectedIntervenant] = useState(null);
  const isIntervenant = user?.role === 3;
  const aujourdhuiDate = useMemo(() => startOfDay(new Date()), []);
  const aujourdhuiStr = useMemo(
    () => format(aujourdhuiDate, 'yyyy-MM-dd'),
    [aujourdhuiDate]
  );
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const rolePath = useMemo(() => {
    const isAdmin = user?.role === 1 || user?.role === 2;
    return isAdmin ? 'admin' : 'intervenant';
  }, [user]);

  useEffect(() => {
    const fetchIntervenants = async () => {
      if (!token) return;

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        };

        const res = await axios.get(
          `${apiUrl}/${rolePath}/intervenant/l/list_intervenant`,
          config
        );

        setIntervenants(res.data.result);
      } catch (err) {
        console.error('Erreur intervenants', err);
      }
    };

    fetchIntervenants();
  }, [apiUrl, rolePath, token]);
  // 1. Chargement des données (API)
  useEffect(() => {
    const fetchAllData = async () => {
      if (!token) return;

      try {
        setLoading(true);

        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        };

        const [responseOffres, responseReservations] = await Promise.all([
          axios.get(
            `${apiUrl}/${rolePath}/offer/g/lo/list_offer${
              selectedIntervenant ? `?intervenantId=${selectedIntervenant}` : ''
            }`,
            config
          ),

          axios.get(
            `${apiUrl}/${rolePath}/offer/resevration/l/all${
              selectedIntervenant ? `?intervenantId=${selectedIntervenant}` : ''
            }`,
            config
          ),
        ]);

        const mappedOffres = responseOffres.data.result.map((item) => ({
          id: item.id_offre,
          nom: item.info_offre?.titre_offre || 'Sans titre',
          intervenant: item.intervenant
            ? `${item.intervenant.prenom_user} ${item.intervenant.nom_user}`
            : null,
          intervenantId: item.intervenant?.id_user || null,
          color: getColorFromId(item.id_offre),
        }));

        const mappedReservations = responseReservations.data.result.map(
          (res) => {
            const start = parseISO(res.start_time_reservation);
            const end = parseISO(res.end_time_reservation);

            return {
              id: res.id_offre_reservation,
              offreId: Number(res.id_offre),
              dateKey: format(start, 'yyyy-MM-dd'),
              rawStart: res.start_time_reservation,
              rawEnd: res.end_time_reservation,
              heureDebut: format(start, 'HH:mm'),
              heureFin: format(end, 'HH:mm'),
              client: res.client || null,
              intervenant: res.user
                ? `${res.user.prenom_user} ${res.user.nom_user}`
                : 'Non assigné',
              status: res.status_reservation,
              originalData: res,
            };
          }
        );

        setOffres(mappedOffres);
        setReservations(mappedReservations);
      } catch (error) {
        console.error('Erreur API:', error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [apiUrl, rolePath, token, selectedIntervenant]);

  const formatClientName = (client) => {
    if (!client) return '—';

    if (typeof client === 'string') return client;

    return isIntervenant
      ? `${client?.prenom_client || ''}`.trim()
      : `${client?.prenom_client || ''} ${client?.nom_client || ''}`.trim();
  };
  // Fonction pour attribuer une couleur stable à chaque offre
  const getColorFromId = (id) => {
    const palette = [
      '#4318FF',
      '#01B574',
      '#FFB547',
      '#EE5D50',
      '#00B5DD',
      '#7551FF',
      '#FF5B87',
      '#22C55E',
      '#F97316',
      '#3B82F6',
      '#A855F7',
      '#E11D48',
      '#14B8A6',
      '#F59E0B',
      '#6366F1',
      '#EC4899',
    ];

    // hash simple stable
    let hash = 0;
    const str = String(id);

    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }

    const index = Math.abs(hash) % palette.length;
    return palette[index];
  };

  // 2. Logique de la Timeline
  const joursTimeline = useMemo(() => {
    const jours = eachDayOfInterval({
      start: subDays(aujourdhuiDate, 30),
      end: addDays(aujourdhuiDate, 365),
    });
    return jours.map((j) => ({
      dateObj: j,
      isoStr: format(j, 'yyyy-MM-dd'),
      moisStr: format(j, 'MMMM yyyy', { locale: fr }),
    }));
  }, [aujourdhuiDate]);

  const indexToday = joursTimeline.findIndex((j) => j.isoStr === aujourdhuiStr);

  const largeurCellule = useMemo(() => {
    if (vue === 1) return 240; // 👈 très large pour lisibilité
    if (vue === 7) return 160;
    if (vue === 15) return 120;
    return 90;
  }, [vue]);

  // 3. Gestion du Scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const visibleDayIndex = Math.round(scrollLeft / largeurCellule);

    if (joursTimeline[visibleDayIndex]) {
      const mois = joursTimeline[visibleDayIndex].moisStr;
      setCurrentMonth(mois.charAt(0).toUpperCase() + mois.slice(1));
    }
  }, [joursTimeline, largeurCellule]);

  const scrollByDays = (direction, daysCount = vue) => {
    const distance = daysCount * largeurCellule;
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (!loading && scrollContainerRef.current && indexToday !== -1) {
      scrollContainerRef.current.scrollLeft = indexToday * largeurCellule;
      handleScroll();
    }
  }, [loading, indexToday, largeurCellule, handleScroll]);

  // 4. Handlers
  const handleReservationClick = (res, offre) => {
    setSelectedReservation({ ...res, offre });
  };
  const handleClose = () => {
    setSelectedReservation(null);
  };

  const filteredReservations = useMemo(() => {
    if (!selectedIntervenant) return reservations;

    return reservations.filter(
      (r) =>
        String(r.originalData?.user?.id_user) === String(selectedIntervenant)
    );
  }, [reservations, selectedIntervenant]);

  const filteredOffres = useMemo(() => {
    if (!selectedIntervenant) return offres;

    return offres.filter(
      (o) => String(o.intervenantId) === String(selectedIntervenant)
    );
  }, [offres, selectedIntervenant]);

  const statusConfig = {
    approved: { label: 'Confirmé', color: '#10B981', bg: '#ECFDF5' },
    pending: { label: 'En attente', color: '#F59E0B', bg: '#FFFBEB' },
    cancelled: { label: 'Annulé', color: '#EF4444', bg: '#FEF2F2' },
    completed: { label: 'Terminé', color: '#6366F1', bg: '#EEF2FF' },
    default: { label: 'Inconnu', color: '#94A3B8', bg: '#F8FAFC' },
  };

  const currentStatusKey =
    selectedReservation?.originalData?.status_reservation;

  const status = statusConfig[currentStatusKey] || statusConfig.default;
  const getStatus = (key) => statusConfig[key] || statusConfig.default;
  const handleStatusChange = async (reservationId, newStatus) => {
    if (!newStatus) {
      toast.error('Statut invalide');
      return;
    }

    const previousStatus =
      selectedReservation?.originalData?.status_reservation;

    try {
      // 🔥 Optimistic UI (liste + modal si ouvert)
      const updateLocalState = (prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          originalData: {
            ...prev.originalData,
            status_reservation: newStatus,
          },
        };
      };

      setSelectedReservation(updateLocalState);

      setReservations((prev) =>
        prev.map((r) =>
          r.id === reservationId
            ? {
                ...r,
                status: newStatus,
                originalData: {
                  ...r.originalData,
                  status_reservation: newStatus,
                },
              }
            : r
        )
      );

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      };

      await axios.post(
        `${apiUrl}/${rolePath}/reservations/update-status/up/${reservationId}`,
        {
          status: newStatus, // ✅ IMPORTANT (backend requirement)
        },
        config
      );

      toast.success('Statut mis à jour avec succès');
    } catch (err) {
      console.error(err);

      // 🔥 rollback UI propre
      setSelectedReservation((prev) => ({
        ...prev,
        originalData: {
          ...prev.originalData,
          status_reservation: previousStatus,
        },
      }));

      setReservations((prev) =>
        prev.map((r) =>
          r.id === reservationId
            ? {
                ...r,
                status: previousStatus,
                originalData: {
                  ...r.originalData,
                  status_reservation: previousStatus,
                },
              }
            : r
        )
      );

      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Erreur lors de la mise à jour du statut';

      toast.error(message);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#4318FF' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: '100vh' }}>
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: '#1B254B', letterSpacing: '-0.5px' }}
          >
            Gestion des Réservations
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <CalendarMonth sx={{ color: '#4318FF', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#4318FF' }}
            >
              {currentMonth}
            </Typography>
            <Typography
              sx={{ color: '#A3AED0', fontWeight: 600, fontSize: '0.85rem' }}
            >
              • Vue {vue} jours
            </Typography>
          </Stack>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          bgcolor="white"
          borderRadius="16px"
          p={0.75}
          boxShadow="0px 10px 30px rgba(112, 144, 176, 0.12)"
          sx={{ border: '1px solid #E0E5F2', gap: 0.5 }}
        >
          <IconButton onClick={() => scrollByDays('left')} size="small">
            <KeyboardDoubleArrowLeft />
          </IconButton>
          <IconButton onClick={() => scrollByDays('left', 1)} size="small">
            <ChevronLeft />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          <ToggleButtonGroup
            value={vue}
            exclusive
            onChange={(e, v) => v && setVue(v)}
            size="small"
            sx={{ border: 'none' }}
          >
            {[1, 7, 15, 30].map((v) => (
              <ToggleButton
                key={v}
                value={v}
                sx={{
                  border: 'none',
                  fontWeight: 700,
                  borderRadius: '10px !important',
                  px: 2,
                  textTransform: 'none',
                }}
              >
                {v === 1 ? '1j' : `${v}j`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          <IconButton onClick={() => scrollByDays('right', 1)} size="small">
            <ChevronRight />
          </IconButton>
          <IconButton onClick={() => scrollByDays('right')} size="small">
            <KeyboardDoubleArrowRight />
          </IconButton>

          <Button
            variant="contained"
            disableElevation
            onClick={() =>
              scrollContainerRef.current.scrollTo({
                left: indexToday * largeurCellule,
                behavior: 'smooth',
              })
            }
            sx={{
              ml: 1,
              bgcolor: '#4318FF',
              borderRadius: '12px',
              fontWeight: 800,
              textTransform: 'none',
            }}
          >
            Aujourd'hui
          </Button>
        </Stack>
      </Stack>

      <FormControl size="small" sx={{ minWidth: 240 }}>
        {!isIntervenant && (
          <Select
            value={selectedIntervenant || ''}
            onChange={(e) => setSelectedIntervenant(e.target.value)}
            displayEmpty
            // On remplace l'icône de flèche par défaut pour un look plus moderne
            IconComponent={FilterListIcon}
            renderValue={(selected) => {
              if (selected === '') {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#64748B',
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: 18 }} />
                    <span>Tous les intervenants</span>
                  </Box>
                );
              }
              const intervenant = intervenants.find(
                (i) => i.id_user === selected
              );
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: '#3B82F6',
                    }}
                  >
                    {intervenant?.prenom_user[0]}
                  </Avatar>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {intervenant?.prenom_user} {intervenant?.nom_user}
                  </Typography>
                </Box>
              );
            }}
            sx={{
              bgcolor: 'white',
              borderRadius: '12px',
              transition: 'all 0.2s',
              border: '1px solid #E2E8F0',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, // On gère la bordure nous-mêmes
              '&:hover': {
                bgcolor: '#F8FAFC',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
              },
              '& .MuiSelect-icon': {
                right: 12,
                color: '#94A3B8',
                fontSize: '1.2rem',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '12px',
                  mt: 1,
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                  border: '1px solid #F1F5F9',
                  maxHeight: 300,
                },
              },
            }}
          >
            <MenuItem
              value=""
              sx={{ color: '#64748B', fontStyle: 'italic', py: 1.5 }}
            >
              Aucun filtre (Tous)
            </MenuItem>

            {intervenants.map((i) => (
              <MenuItem
                key={i.id_user}
                value={i.id_user}
                sx={{
                  py: 1,
                  px: 2,
                  mx: 0.5,
                  borderRadius: '8px',
                  '&.Mui-selected': { bgcolor: '#EFF6FF !important' },
                  '&:hover': { bgcolor: '#F1F5F9' },
                }}
              >
                <ListItemIcon>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                    {i.prenom_user[0]}
                    {i.nom_user[0]}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={`${i.prenom_user} ${i.nom_user}`}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>
      {/* GRID CONTAINER */}
      <Paper
        sx={{
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1px solid #E0E5F2',
          boxShadow: '0px 20px 50px rgba(112, 144, 176, 0.08)',
        }}
      >
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{
            overflowX: 'auto',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            bgcolor: 'white',
          }}
        >
          {/* INDICATEUR AUJOURD'HUI */}
          {indexToday !== -1 && (
            <Box
              sx={{
                position: 'absolute',
                left: SIDEBAR_WIDTH + indexToday * largeurCellule,
                top: 0,
                bottom: 0,
                width: '2px',
                bgcolor: '#4318FF',
                zIndex: 50,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* EN-TÊTE DES JOURS */}
          <Box sx={{ display: 'flex', minWidth: 'max-content' }}>
            <Box
              sx={{
                minWidth: SIDEBAR_WIDTH,
                position: 'sticky',
                left: 0,
                zIndex: 60,
                bgcolor: '#F8FAFD',
                borderBottom: '2px solid #E0E5F2',
                borderRight: '2px solid #E0E5F2',
                p: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  color: '#A3AED0',
                  letterSpacing: '1px',
                }}
              >
                OFFRES
              </Typography>
            </Box>
            {joursTimeline.map((jour) => (
              <Box
                key={jour.isoStr}
                sx={{
                  minWidth: largeurCellule,
                  py: 2.5,
                  textAlign: 'center',
                  bgcolor:
                    jour.isoStr === aujourdhuiStr
                      ? alpha('#4318FF', 0.04)
                      : '#F8FAFD',
                  borderBottom: '2px solid #E0E5F2',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    color:
                      jour.isoStr === aujourdhuiStr ? '#4318FF' : '#707EAE',
                    textTransform: 'uppercase',
                  }}
                >
                  {format(jour.dateObj, 'EEE dd', { locale: fr })}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* LIGNES DES OFFRES */}
          {filteredOffres.map((offre) => (
            <Box
              key={offre.id}
              sx={{ display: 'flex', minWidth: 'max-content' }}
            >
              <Box
                sx={{
                  minWidth: SIDEBAR_WIDTH,
                  position: 'sticky',
                  left: 0,
                  zIndex: 30,
                  bgcolor: 'white',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  borderRight: '2px solid #E0E5F2',
                  borderBottom: '1px solid #F4F7FE',
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 24,
                    bgcolor: offre.color,
                    borderRadius: 1,
                    mr: 2,
                  }}
                />
                <Typography
                  sx={{ fontWeight: 700, color: '#1B254B', fontSize: '0.9rem' }}
                >
                  {offre.nom}
                  {offre.intervenant && (
                    <Typography sx={{ fontWeight: 400, color: '#707EAE' }}>
                      {offre.intervenant}
                    </Typography>
                  )}

                  <Button
                    size="small"
                    onClick={() => {
                      const today = aujourdhuiStr;
                      const todayReservations = filteredReservations.filter(
                        (r) =>
                          String(r.offreId) === String(offre.id) &&
                          r.dateKey === today
                      );

                      setSelectedDay({
                        offre,
                        jour: joursTimeline.find((j) => j.isoStr === today),
                        reservations: todayReservations,
                      });
                    }}
                    startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                    sx={{
                      alignSelf: 'flex-start', // Important pour ne pas qu'il prenne toute la largeur
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      px: 2,
                      py: 0.6,
                      borderRadius: '8px',
                      bgcolor: alpha(offre.color, 0.08),
                      border: `1px solid ${alpha(offre.color, 0.3)}`,
                      color: offre.color,
                      transition: '0.2s',
                      '&:hover': {
                        bgcolor: alpha(offre.color, 0.15),
                        border: `1px solid ${offre.color}`,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    Planning du jour
                  </Button>
                </Typography>
              </Box>
              {joursTimeline.map((jour) => {
                const cellReservations = filteredReservations.filter(
                  (res) =>
                    String(res.offreId) === String(offre.id) &&
                    res.dateKey === jour.isoStr
                );
                const MAX_VISIBLE = 3;

                const visible = cellReservations.slice(0, MAX_VISIBLE);
                const hidden = cellReservations.slice(MAX_VISIBLE);

                return (
                  <Box
                    key={jour.isoStr}
                    sx={{
                      minWidth: largeurCellule,
                      minHeight: CELL_HEIGHT,
                      borderRight: '1px solid #F4F7FE',
                      borderBottom: '1px solid #F4F7FE',
                      position: 'relative',
                    }}
                  >
                    <Stack spacing={0.5} sx={{ p: 0.5 }}>
                      {visible.map((res) => (
                        <Paper
                          key={res.id}
                          onClick={() => handleReservationClick(res, offre)}
                          sx={{
                            bgcolor: alpha(offre.color, 0.12),
                            border: `1px solid ${offre.color}`,
                            px: 1,
                            py: 0.5,
                            borderRadius: '6px',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ fontWeight: 800 }}>
                            {res.heureDebut} - {res.heureFin}
                          </div>

                          <div>
                            {res.client
                              ? formatClientName(res.client)
                              : 'Client inconnu'}
                          </div>
                        </Paper>
                      ))}

                      {hidden.length > 0 && (
                        <Button
                          size="small"
                          onClick={() =>
                            setSelectedDay({
                              offre,
                              jour,
                              reservations: cellReservations, // 🔥 IMPORTANT : pas sorted global
                            })
                          }
                        >
                          +{hidden.length} autres
                        </Button>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>

      <Dialog
        open={!!selectedDay}
        onClose={() => setSelectedDay(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid #F1F5F9',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, color: '#1E293B' }}>
              Planning du jour
            </Typography>
            <Typography sx={{ color: '#64748B' }}>
              {selectedDay?.jour?.isoStr}
            </Typography>
          </Box>

          <IconButton onClick={() => setSelectedDay(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent sx={{ p: 3 }}>
          {(() => {
            if (!selectedDay) return null;

            // 🔥 SOURCE UNIQUE = reservations global
            const dayReservations = reservations.filter(
              (r) =>
                r.dateKey === selectedDay.jour.isoStr &&
                String(r.offreId) === String(selectedDay.offre.id)
            );

            if (!dayReservations.length) {
              return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <EventBusyIcon sx={{ fontSize: 48, color: '#E2E8F0' }} />
                  <Typography sx={{ color: '#94A3B8' }}>
                    Aucune réservation prévue ce jour
                  </Typography>
                </Box>
              );
            }

            return (
              <Stack spacing={2}>
                {dayReservations.map((res) => {
                  const currentStatusKey =
                    res?.originalData?.status_reservation;

                  const status = getStatus(currentStatusKey);

                  return (
                    <Paper
                      key={res.id}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: '0.2s',
                        '&:hover': { bgcolor: '#F8FAFC' },
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          bgcolor: status.color,
                        },
                      }}
                    >
                      {/* INFOS */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
                          {res.heureDebut} — {res.heureFin}
                        </Typography>

                        <Typography sx={{ fontWeight: 600 }}>
                          {res.client
                            ? formatClientName(res.client)
                            : 'Client inconnu'}
                        </Typography>

                        <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                          Intervenant : {res.intervenant}
                        </Typography>

                        {/* SELECT STATUS */}
                        <Select
                          size="small"
                          value={currentStatusKey || 'pending'}
                          onChange={(e) =>
                            handleStatusChange(res.id, e.target.value)
                          }
                          sx={{
                            mt: 1,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            borderRadius: '6px',
                            bgcolor: status.bg,
                            color: status.color,
                            width: '150px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="approved">Confirmé</MenuItem>
                          <MenuItem value="pending">En attente</MenuItem>
                          <MenuItem value="cancelled">Annulé</MenuItem>
                          <MenuItem value="completed">Terminé</MenuItem>
                        </Select>
                      </Box>

                      {/* ACTION */}
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<ChevronRightIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReservationClick(res, selectedDay.offre);
                        }}
                        sx={{
                          bgcolor: '#F1F5F9',
                          color: '#475569',
                          '&:hover': {
                            bgcolor: '#3B82F6',
                            color: '#fff',
                          },
                        }}
                      >
                        Détails
                      </Button>
                    </Paper>
                  );
                })}
              </Stack>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selectedReservation}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(216, 18, 18, 0.15)',
            backgroundImage: 'none',
            overflow: 'hidden',
          },
        }}
      >
        {selectedReservation && (
          <>
            {/* BARRE DE TITRE PROFESSIONNELLE */}
            <DialogTitle
              sx={{
                p: 3,
                bgcolor: '#3311CC',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {' '}
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, fontSize: '1.2rem' }}
                >
                  Dossier de Réservation
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, letterSpacing: 1 }}
                >
                  RÉF :{' '}
                  {selectedReservation?.originalData?.id_offre_reservation} •
                  SESSION CONFIRMÉE
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1,
                    flexWrap: 'wrap', // 🔥 important pour mobile
                  }}
                >
                  <Select
                    size="small"
                    value={currentStatusKey || 'pending'}
                    onChange={(e) =>
                      handleStatusChange(
                        selectedReservation.originalData.id_offre_reservation,
                        e.target.value
                      )
                    }
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      borderRadius: '6px',
                      bgcolor: status.bg,
                      color: status.color,
                      minWidth: '160px',

                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiSvgIcon-root': {
                        color: status.color,
                      },
                    }}
                  >
                    <MenuItem value="approved">Confirmé</MenuItem>
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="cancelled">Annulé</MenuItem>
                    <MenuItem value="completed">Terminé</MenuItem>
                  </Select>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.7rem',
                      lineHeight: 1.4,
                    }}
                  >
                    Gérez le cycle de la réservation : validez, annulez ou
                    marquez comme terminée.
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4, bgcolor: '#F7FAFC' }}>
              <Stack spacing={4}>
                {/* BANDEAU INFO RÉUNION (STYLE DASHBOARD) */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={4}
                    divider={<Divider orientation="vertical" flexItem />}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#718096',
                          fontWeight: 700,
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        DATE DE LA SÉANCE
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonth
                          sx={{ color: '#4A5568', fontSize: 20 }}
                        />
                        <Typography sx={{ fontWeight: 700, color: '#2D3748' }}>
                          {new Date(
                            selectedReservation?.originalData
                              ?.info_conference[0]?.start_date_conf
                          ).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#718096',
                          fontWeight: 700,
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        CRÉNEAU HORAIRE
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTime sx={{ color: '#4A5568', fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 800, color: '#2D3748' }}>
                          {new Date(
                            selectedReservation?.originalData
                              ?.info_conference[0]?.start_date_conf
                          ).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          <span style={{ margin: '0 8px', color: '#CBD5E0' }}>
                            —
                          </span>
                          {new Date(
                            selectedReservation?.originalData
                              ?.info_conference[0]?.end_date_conf
                          ).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      bgcolor: '#EDF2F7',
                      p: 1.5,
                      borderRadius: '8px',
                      border: '1px dashed #CBD5E0',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#4A5568',
                        fontWeight: 800,
                        display: 'block',
                        textAlign: 'center',
                      }}
                    >
                      ID RÉUNION ZOOM
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        color: '#2D3748',
                      }}
                    >
                      {
                        selectedReservation?.originalData?.info_conference[0]
                          ?.id_meeting_conf
                      }
                    </Typography>
                  </Box>
                </Box>

                {/* GRILLE DES ACTEURS */}
                <Grid container spacing={3}>
                  {/* SECTION CLIENT */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0',
                        height: '100%',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Typography
                          variant="overline"
                          sx={{ color: '#718096', fontWeight: 800 }}
                        >
                          Client / Bénéficiaire
                        </Typography>
                        {/* Badge Premium si abonné */}
                        {selectedReservation?.originalData?.client?.etat_abn ===
                          1 && (
                          <Chip
                            label="PREMIUM"
                            size="small"
                            icon={
                              <Star
                                sx={{
                                  fontSize: '14px !important',
                                  color: '#B7791F',
                                }}
                              />
                            }
                            sx={{
                              bgcolor: '#FEFCBF',
                              color: '#B7791F',
                              fontWeight: 900,
                              fontSize: '0.65rem',
                              height: 20,
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 2, mb: 3 }}
                        alignItems="center"
                      >
                        <Avatar
                          src={`${process.env.REACT_APP_API_URL_FILE}/${selectedReservation?.originalData?.client?.photo_client}`}
                          sx={{
                            width: 64,
                            height: 64,
                            border: '3px solid #F4F7FE',
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: '#1A202C',
                              fontSize: '1.1rem',
                            }}
                          >
                            {isIntervenant
                              ? selectedReservation?.originalData?.client
                                  ?.prenom_client
                              : `${selectedReservation?.originalData?.client?.prenom_client || ''} ${selectedReservation?.originalData?.client?.nom_client || ''}`}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#4318FF', fontWeight: 600 }}
                          >
                            {!isIntervenant &&
                              selectedReservation?.originalData?.client
                                ?.email_client}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Infos complémentaires Client */}
                      {!isIntervenant && (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: '#F7FAFC',
                            borderRadius: '8px',
                            border: '1px solid #EDF2F7',
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#A0AEC0',
                                  fontWeight: 700,
                                  display: 'block',
                                }}
                              >
                                TÉLÉPHONE
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: '#4A5568' }}
                              >
                                {selectedReservation?.originalData?.client
                                  ?.phone_client || 'Non renseigné'}
                              </Typography>
                            </Grid>

                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#A0AEC0',
                                  fontWeight: 700,
                                  display: 'block',
                                }}
                              >
                                LOCALISATION
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: '#4A5568' }}
                              >
                                {
                                  selectedReservation?.originalData?.client
                                    ?.ville_client
                                }{' '}
                                (
                                {
                                  selectedReservation?.originalData?.client
                                    ?.cp_client
                                }
                                )
                              </Typography>
                            </Grid>

                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#A0AEC0',
                                  fontWeight: 700,
                                  display: 'block',
                                }}
                              >
                                ADRESSE
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: '#718096',
                                  fontSize: '0.85rem',
                                }}
                              >
                                {
                                  selectedReservation?.originalData?.client
                                    ?.addresse_client
                                }
                                ,{' '}
                                {
                                  selectedReservation?.originalData?.client
                                    ?.province_client
                                }
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* SECTION PRATICIEN */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: '#1A202C',
                        borderRadius: '12px',
                        color: 'white',
                        height: '100%',
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 800 }}
                      >
                        Praticien / Expert
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 2, mb: 3 }}
                        alignItems="center"
                      >
                        <Avatar
                          src={`${apiUrl}/storage/workshop/${selectedReservation?.originalData?.user?.photo_user}`}
                          sx={{
                            width: 64,
                            height: 64,
                            border: '3px solid rgba(255,255,255,0.2)', // Légèrement plus épais pour le contraste sur fond noir
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)', // Optionnel : ajoute un petit relief
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{ fontWeight: 800, fontSize: '1.1rem' }}
                          >
                            {
                              selectedReservation?.originalData?.user
                                ?.prenom_user
                            }{' '}
                            {selectedReservation?.originalData?.user?.nom_user}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#63B3ED',
                              fontWeight: 700,
                              bgcolor: 'rgba(99, 179, 237, 0.1)',
                              px: 1,
                              borderRadius: '4px',
                              display: 'inline-block',
                            }}
                          >
                            {
                              selectedReservation?.originalData?.user
                                ?.title_user
                            }
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Infos complémentaires Praticien */}
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.5)',
                                fontWeight: 700,
                              }}
                            >
                              CONTACT DIRECT
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {selectedReservation?.originalData?.user
                                ?.phone_user || 'N/A'}
                            </Typography>
                          </Box>
                          <Divider
                            sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.5)',
                                fontWeight: 700,
                              }}
                            >
                              SPÉCIALITÉ
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: '#63B3ED' }}
                            >
                              Expert certifié
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </Grid>

                  {/* ========================= */}
                  {/* CHAT CONVERSATION */}
                  {/* ========================= */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mt: 4 }}>
                      {selectedReservation?.originalData?.conversation
                        ?.id_conv ? (
                        <ChatReservation
                          conversation={
                            selectedReservation?.originalData?.conversation
                          }
                        />
                      ) : (
                        <Box
                          sx={{
                            p: 3,
                            bgcolor: '#fff',
                            borderRadius: 2,
                            border: '1px dashed #CBD5E0',
                            textAlign: 'center',
                          }}
                        >
                          <Typography color="text.secondary">
                            Aucune conversation liée à cette réservation
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* DÉTAILS DE LA PRESTATION */}
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      color: '#2D3748',
                      borderLeft: '4px solid #4318FF',
                      pl: 2,
                    }}
                  >
                    Détails de la prestation
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Ligne 1 : Nom, Honoraires et Durée */}
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#718096', fontWeight: 700 }}
                      >
                        NOM DE L'OFFRE
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        {
                          selectedReservation?.originalData?.offre?.info_offre
                            ?.titre_offre
                        }
                      </Typography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#718096', fontWeight: 700 }}
                      >
                        HONORAIRES
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: '1.2rem',
                          color: '#38A169',
                        }}
                      >
                        {
                          selectedReservation?.originalData?.offre?.info_offre
                            ?.prix_offre
                        }{' '}
                        €
                      </Typography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#718096', fontWeight: 700 }}
                      >
                        DURÉE
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
                        {
                          selectedReservation?.originalData?.offre?.info_offre
                            ?.duree_offre
                        }{' '}
                        min
                      </Typography>
                    </Grid>

                    {/* Ligne 2 : Description (Ajoutée) */}
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#718096', fontWeight: 700 }}
                      >
                        DESCRIPTION
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: '#4A5568',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-line', // Préserve les sauts de ligne si présents dans le texte
                        }}
                      >
                        {selectedReservation?.originalData?.offre?.info_offre
                          ?.description_offre || 'Aucune description fournie.'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* ACTIONS ACTIONS */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        selectedReservation?.originalData?.info_conference[0]
                          ?.link_client_conf,
                        '_blank'
                      )
                    }
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 3,
                      color: '#4A5568',
                      borderColor: '#E2E8F0',
                    }}
                  >
                    Lien invité
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() =>
                      window.open(
                        selectedReservation?.originalData?.info_conference[0]
                          ?.link_intervenant_conf,
                        '_blank'
                      )
                    }
                    startIcon={<VideoCameraFront />}
                    sx={{
                      bgcolor: '#4318FF',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      '&:hover': { bgcolor: '#3311CC' },
                    }}
                  >
                    Lancer la conférence
                  </Button>
                </Stack>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PlanningPro;
